import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Globe, Repeat } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/* Admin: every visitor with IP, location, how many times they came back */
const VisitorsPanel = ({ adminKey }) => {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [onlyReturning, setOnlyReturning] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/analytics/visitors`)
      .then(({ data }) => setItems(data.visitors || []))
      .catch(() => setErr("Failed to load visitors."));
  }, [adminKey]);

  if (err) return <div className="text-red-400 text-sm" data-testid="visitors-error">{err}</div>;
  if (!items)
    return (
      <div className="flex items-center gap-3 text-zinc-400" data-testid="visitors-loading">
        <Loader2 className="animate-spin" size={16} /> Loading visitors…
      </div>
    );

  const list = onlyReturning ? items.filter((v) => v.visits > 1) : items;
  const returning = items.filter((v) => v.visits > 1).length;

  return (
    <div className="space-y-3" data-testid="visitors-panel">
      <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-zinc-500">
        <span>{items.length} recent visitors · {returning} returned at least once</span>
        <button
          type="button"
          onClick={() => setOnlyReturning((v) => !v)}
          className={`ml-auto rounded-full border px-3 py-1.5 tracking-[0.15em] transition-colors ${onlyReturning ? "border-[#E5C56F]/50 text-[#E5C56F] bg-[#E5C56F]/10" : "border-white/15 text-zinc-400 hover:text-zinc-200"}`}
          data-testid="visitors-returning-toggle"
        >
          <Repeat size={11} className="inline mr-1.5 -mt-0.5" /> {onlyReturning ? "Showing returning only" : "Show returning only"}
        </button>
      </div>
      {list.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-zinc-500" data-testid="visitors-empty">
          No visitors match.
        </div>
      )}
      {list.map((v, i) => {
        const place = [v.city, v.country].filter(Boolean).join(", ") || "Location unknown";
        return (
          <div key={v.visitor_id + i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-3" data-testid={`visitor-row-${i}`}>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] shrink-0 ${v.visits > 1 ? "border-[#E5C56F]/40 bg-[#E5C56F]/10 text-[#E5C56F]" : "border-white/15 bg-white/[0.03] text-zinc-400"}`} data-testid={`visitor-visits-${i}`}>
              {v.visits > 1 ? `${ordinal(v.visits)} visit` : "First visit"}
            </span>
            <div className="min-w-0 text-sm text-zinc-200">
              <span className="text-zinc-400">Visitor #{v.visitor_id}</span>{" "}
              from <span className="text-zinc-100">{place}</span>
              <span className="text-zinc-500"> · {v.device}</span>
              <div className="text-[11px] text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                <span className="inline-flex items-center gap-1"><Globe size={11} /> IP <span className="text-zinc-300 font-mono" data-testid={`visitor-ip-${i}`}>{v.ip || "not recorded"}</span>{v.ip_count > 1 ? <span className="text-zinc-600">(+{v.ip_count - 1} more)</span> : null}</span>
                <span>{v.pages} page view{v.pages === 1 ? "" : "s"} total</span>
                <span>last on <span className="text-purple-300">{v.last_path}</span></span>
                {v.referrer ? <span>via {v.referrer}</span> : null}
              </div>
            </div>
            <div className="ml-auto text-[11px] text-zinc-500 shrink-0 text-right">
              <div>last {fmt(v.last_seen)}</div>
              {v.visits > 1 ? <div className="text-zinc-600">first {fmt(v.first_seen)}</div> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VisitorsPanel;
